import React, { HtmlHTMLAttributes, useState } from 'react';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { ProductType, SubscriptionPeriod } from '@meitu/subscribe-core';
import { getDisplayPrice, getIncomeSource, getPayChannels, isWeb } from '@/utils/vip';
import { openBrowser } from '@/utils/native';
import { getUrlParam } from '@/utils';
import WeChartIcon from '@/assets/image/vip/weChat.png';
import AlipayIcon from '@/assets/image/vip/alipay.png';
import {
  PurchaseScene,
  SubscribeType,
  isTeamSubscription,
  purchaseScene,
  subscribeType,
} from '@/constants';
import { Checkbox, Tooltip } from 'antd';
import { PromotionType, TabType } from '@/constants/subscription';
import { VipInfo } from '@/services/user/index.d';
import { useAtom } from 'jotai';
import {
  auditVersionStatusAtom,
  curProductAtom,
  curTabAtom,
  hasCmbChinaToPaidAtom,
  hasFetchUserInfoAtom,
  loadingAtom,
  qrCodeUrlAtom,
  seatsAtom,
  userInfoAtom,
  vipInfoAtom,
} from '@/store';
import { track } from '@/utils/common';
import defaultQrCode from '@/assets/image/vip/default-qrcode.png';

import styles from './index.module.css';

const channelsMap: any = {
  weixin: '微信',
  alipay: '支付宝',
};

interface IProps extends HtmlHTMLAttributes<HTMLDivElement> {
  handleLogin?: () => void;
  onRefresh?: () => void;
  useMeidou?: boolean;
}

const ProductPay: React.FC<IProps> = (props: IProps) => {
  const { children, handleLogin, onRefresh, useMeidou } = props;
  const type = getUrlParam('type');
  const [checked, setChecked] = useState<boolean>(false);

  const [loading] = useAtom(loadingAtom);
  const [curTab] = useAtom(curTabAtom);
  const [vipInfo] = useAtom(vipInfoAtom);
  const [seats] = useAtom(seatsAtom);
  const [hasFetchUserInfo] = useAtom(hasFetchUserInfoAtom);
  const [userInfo] = useAtom(userInfoAtom);
  const [curProduct] = useAtom(curProductAtom);
  const [auditVersionStatus] = useAtom(auditVersionStatusAtom);
  const [qrCodeUrl] = useAtom(qrCodeUrlAtom);
  const [hasCmbChinaToPaid] = useAtom(hasCmbChinaToPaidAtom);

  const noLogin = hasFetchUserInfo && !userInfo?.id;

  const handleJumpTerm = () => {
    let params = {};

    if (isWeb) {
      params = {
        class: 'vip_tk',
        name_id: curProduct?.productId,
      };
    } else {
      params = {
        category: type === 'svip' ? '批处理条款' : '会员条款',
        product_id: curProduct?.productId,
      };
    }

    track({
      event_id: 'vip_page_click',
      event_name: '会员落地页点击',
      params,
    });

    let prefix = process.env.REACT_APP_ENV;

    if (process.env.REACT_APP_ENV === 'release') {
      prefix = '';
    }

    let url = isWeb
      ? 'https://pro.meitu.com/posterlabs/rules/global/vipServiceAgreementPlusWeb.html'
      : 'https://pro.meitu.com/posterlabs/rules/global/vipServiceAgreement-web.html';

    if (useMeidou || subscribeType === SubscribeType.MEIDOUPACKAGE) {
      url = 'https://www.designkit.cn/meidou-service';
    } else if (subscribeType === SubscribeType.CONSUMABLE && curTab === TabType.CUTOUT) {
      url = `https://pro.meitu.com/posterlabs/rules/web/koutu-service.html`;
    } else if (type === 'svip') {
      // 批处理
      url = 'https://pc.meitu.com/terms-svip';
    }

    if (isWeb) {
      window.open(url);
    } else {
      openBrowser(url);
    }
  };

  const handleJumpAuth = () => {
    const url = 'https://pro.meitu.com/posterlabs/rules/web/powerOfAttorney.html';

    if (isWeb) {
      window.open(url);
    } else {
      openBrowser(url);
    }
  };

  // 页面第一次加载商品并请求交易二维码的时候才需要展示骨架屏
  const showSkeleton = !curProduct && !qrCodeUrl;

  const promotion = (curProduct?.promotions || [])[0] || {};
  // 是否免费试用商品, 2、4
  const isFreeTrial =
    promotion.promotionType === PromotionType.RecommendedFreeTrial ||
    promotion.promotionType === PromotionType.SalesFreeTrial;
  // 免费试用文案
  let freeTrialText = '';
  if (isFreeTrial) {
    let unit = '';
    switch (promotion?.promotionDuration?.period) {
      case 0:
        unit = '天';
        break;
      case 1:
        unit = '月';
        break;
      case 2:
        unit = '季';
        break;
      case 3:
        unit = '年';
        break;
      default:
    }
    freeTrialText = `${promotion.promotionDuration?.duration || ''}${unit}免费试用`;
  }

  const getNewEndDate = () => {
    const duration = curProduct?.subPeriodDuration || 0;
    if (curProduct?.subPeriod === SubscriptionPeriod.Day) {
      return dayjs((vipInfo?.vip_end_time || 0) * 1000)
        .add(duration, 'day')
        .format('YYYY-MM-DD');
    } else if (curProduct?.subPeriod === SubscriptionPeriod.PerMonth) {
      return dayjs((vipInfo?.vip_end_time || 0) * 1000)
        .add(duration, 'month')
        .format('YYYY-MM-DD');
    } else if (curProduct?.subPeriod === SubscriptionPeriod.PerQuarter) {
      return dayjs((vipInfo?.vip_end_time || 0) * 1000)
        .add(duration * 3, 'month')
        .format('YYYY-MM-DD');
    } else if (curProduct?.subPeriod === SubscriptionPeriod.PerYear) {
      return dayjs((vipInfo?.vip_end_time || 0) * 1000)
        .add(duration, 'year')
        .format('YYYY-MM-DD');
    }
  };

  // 支付渠道
  const payChannels: string[] = getPayChannels(curProduct) || [];

  const renderTipText = () => {
    // if (subscribeType === SubscribeType.CONSUMABLE) {
    //   // 单购
    //   const payChannelsText = payChannels
    //     ?.map((item: string) => {
    //       return channelsMap[item];
    //     })
    //     ?.join('、');

    //   return (
    //     <>
    //       {payChannels.includes('weixin') ? (
    //         <img className={styles.icon} src={WeChartIcon} alt="" />
    //       ) : null}
    //       {payChannels.includes('alipay') ? (
    //         <img className={styles.icon} src={AlipayIcon} alt="" />
    //       ) : null}
    //       <span>{`${payChannelsText}扫码${
    //         product?.productType === ProductType.Consumable ? '购买' : '开通'
    //       }`}</span>
    //     </>
    //   );
    // }

    const payChannelsText = payChannels
      ?.filter((item) => (payChannels.length > 1 ? item !== 'weixin' : true))
      ?.map((item: string) => {
        return channelsMap[item];
      })
      ?.join('、');

    return (
      <>
        {payChannels?.length === 1 && payChannels.includes('weixin') ? (
          <img className={styles.icon} src={WeChartIcon} alt="" />
        ) : null}
        {payChannels.includes('alipay') ? (
          <img className={styles.icon} src={AlipayIcon} alt="" />
        ) : null}
        <span>{`${payChannelsText}扫码${
          curProduct?.productType === ProductType.Consumable ? '购买' : '开通'
        }`}</span>
        {payChannels?.length > 1 && payChannels?.includes('weixin') ? (
          <span className={styles.subTip}>
            （同时支持微信扫码
            {curProduct?.productType === ProductType.Consumable ? '购买' : '开通'}）
          </span>
        ) : null}
      </>
    );
  };

  let termText = isWeb ? '美图设计室会员服务协议' : '美图秀秀粉钻SVIP会员协议';
  if (useMeidou) {
    termText = '美豆充值协议';
  } else if (subscribeType === SubscribeType.CONSUMABLE && curTab === TabType.CUTOUT) {
    termText = '抠图服务协议';
  } else if (subscribeType === SubscribeType.MEIDOUPACKAGE) {
    termText = 'AI商拍套餐购买协议';
  } else if (type === 'svip') {
    // 批处理
    termText = '美图秀秀批处理服务协议';
  }

  let payPrice = getDisplayPrice(curProduct).payPrice;

  // 团队版下的支付价计算
  if (isTeamSubscription && curTab === TabType.SUB) {
    // 取第一个优惠信息
    const promotionInfo: any = curProduct?.promotions && curProduct.promotions[0];
    // 是否是阶梯优惠商品类型
    const isTieredDiscounts = promotionInfo?.promotionType === PromotionType.TieredDiscounts;
    // 如果是阶梯优惠，支付价格（promotion_price.price * enjoy_num + (用户选择席位 - enjoy_num) * product_price.price）
    // 如果是非阶梯优惠，支付价格（payPrice * 用户选择席位）
    payPrice = curProduct
      ? isTieredDiscounts
        ? +(
            getDisplayPrice(curProduct).payPrice * promotionInfo.enjoyNum +
            (seats - promotionInfo.enjoyNum) * (+(curProduct?.productPrice?.price || 0) / 100)
          ).toFixed(2)
        : +(getDisplayPrice(curProduct).payPrice * seats).toFixed(2)
      : '';
  }

  return (
    <div
      className={classNames(styles.module, {
        [styles.meidou]: SubscribeType.MEIDOU === subscribeType,
      })}
    >
      <div className={styles.body}>
        {!showSkeleton && qrCodeUrl && (!auditVersionStatus || (auditVersionStatus && checked)) ? (
          <img
            className={styles.qrcode}
            src={hasCmbChinaToPaid ? defaultQrCode : qrCodeUrl}
            alt=""
          />
        ) : (
          <div
            className={classNames(
              styles.qrcode,
              !(noLogin && curProduct?.productId) ? styles.skeleton : styles.noLoginQrcode
            )}
          >
            {noLogin && curProduct?.productId ? (
              <div className={styles.noLogin} onClick={() => handleLogin && handleLogin()}>
                点击登录
              </div>
            ) : loading ? (
              ''
            ) : !checked && auditVersionStatus ? (
              <div className={styles.noLogin}>请先同意协议</div>
            ) : (
              <div className={styles.noLogin} onClick={() => onRefresh && onRefresh()}>
                加载异常
                <br />
                点击刷新
              </div>
            )}
          </div>
        )}

        <div className={styles.info}>
          <div className={classNames(styles.payPrice, showSkeleton && styles.skeleton)}>
            {!isFreeTrial ? (
              <>
                支付：
                <span className={styles.symbol}>{getDisplayPrice(curProduct).moneySymbol}</span>
                <span className={styles.price}>{payPrice}</span>
              </>
            ) : (
              <span className={styles.freeTrialText}>{freeTrialText}</span>
            )}
          </div>
          {/* 团队版续费需要展示 */}
          {isTeamSubscription &&
          curTab === TabType.SUB &&
          purchaseScene === PurchaseScene.NORMAL &&
          vipInfo?.is_vip ? (
            <div className={classNames(styles.expireDate, showSkeleton && styles.skeleton)}>
              购买后延期至：{getNewEndDate()}
            </div>
          ) : null}

          <div className={classNames(styles.tip, showSkeleton && styles.skeleton)}>
            {renderTipText()}
          </div>
          <div className={classNames(styles.term, showSkeleton && styles.skeleton)}>
            {auditVersionStatus ? (
              <Tooltip open={!checked} placement="bottom" title="请先阅读并同意协议">
                <Checkbox
                  checked={checked}
                  style={{ marginRight: 5 }}
                  onChange={(e) => setChecked(e.target.checked)}
                ></Checkbox>
              </Tooltip>
            ) : null}
            <div>
              <>{auditVersionStatus ? '同意' : '支付即视为您已同意'}</>
              <span className={styles.link} onClick={handleJumpTerm}>
                《{termText}》
              </span>
              {isTeamSubscription && curTab === TabType.SUB ? (
                <span className={styles.link} onClick={handleJumpAuth}>
                  《商用授权书》
                </span>
              ) : null}
            </div>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
};

export default React.memo(ProductPay);
